import {
    Button,
    Card,
    FluentProvider,
    MessageBar, MessageBarActions, MessageBarBody, MessageBarTitle,
    SelectTabData,
    SelectTabEvent,
    Spinner,
    tokens,
} from "@fluentui/react-components";
import {lazy, Suspense, useEffect, useMemo, useState} from "react";
import {app} from "@microsoft/teams-js";
import {TeamsAdminCredential} from "./auth/teamsAdminCredential.ts";
import {useTeamsHost} from "./auth/useTeamsHost.ts";
import "react"
import {useAtom, useSetAtom} from "jotai";
import {appIdAtom, errorAtom, selectedTabAtom, teamsAdminCredentialAtom} from "./store.ts";
import {TabNavigation} from "./components/TabNavigation.tsx";
import {SecuredWrapper} from "./components/wrapper/SecuredWrapper.tsx";
import {useTranslation} from "react-i18next";
import {Dismiss16Regular} from "@fluentui/react-icons";
import {
    buildSelfServiceLoginUrl,
    buildSelfServiceSignupUrl,
    type SelfServiceTeamsPrincipal,
} from "./utils/selfServiceUrl.ts";
import {getRuntimeConfig, loadRuntimeConfig} from "./runtimeConfig.ts";
import {buildReloadUrl, isNewerVersion} from "./utils/appVersion.ts";
import {
    applyShellBackground,
    persistTeamsThemeString,
    readTeamsThemeHint,
    removeInitialLoader,
    resolveFluentTheme,
} from "./utils/shellTheme.ts";

const AudiencesTab = lazy(() => import("./components/audiences/AudiencesTab.tsx").then(m => ({ default: m.AudiencesTab })));
const EnrollmentsTab = lazy(() => import("./components/enrollments/EnrollmentsTab.tsx").then(m => ({ default: m.EnrollmentsTab })));
const HomeTab = lazy(() => import("./components/home/HomeTab.tsx").then(m => ({ default: m.HomeTab })));
const SeriesTab = lazy(() => import("./components/series/SeriesTab.tsx").then(m => ({ default: m.SeriesTab })));

const CURRENT_VERSION = __APP_VERSION__;
const VERSION_CHECK_INTERVAL_MS = 10 * 60 * 1000;

export function Bootstrap() {
    const [ready, setReady] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    useEffect(() => {
        loadRuntimeConfig()
            .then(() => setReady(true))
            .catch(() => setLoadError("Failed to load application configuration. Please try again later."));
    }, []);

    const bootstrapTheme = readTeamsThemeHint();

    useEffect(() => {
        if (loadError) {
            applyShellBackground(bootstrapTheme);
            removeInitialLoader();
        }
    }, [loadError, bootstrapTheme]);

    if (loadError) {
        return (
            <FluentProvider theme={bootstrapTheme} style={{background: tokens.colorNeutralBackground3, minHeight: "100vh", padding: "20px"}}>
                <MessageBar intent="error">
                    <MessageBarBody>
                        <MessageBarTitle>Error</MessageBarTitle>
                        {loadError}
                    </MessageBarBody>
                </MessageBar>
            </FluentProvider>
        );
    }

    if (!ready) {
        return null;
    }

    return <App/>;
}

function App() {
    const clientId = getRuntimeConfig().clientId;
    const [selectedTab, setSelectedTab] = useAtom(selectedTabAtom)
    const setAppId = useSetAtom(appIdAtom)
    const [errorMessage, setErrorMessage] = useAtom(errorAtom)
    const setTeamsAdminCredential = useSetAtom(teamsAdminCredentialAtom)
    const [initialized, setInitialized] = useState<boolean>(false);
    const [newVersionAvailable, setNewVersionAvailable] = useState<string | null>(null);
    const [selfServicePrincipal, setSelfServicePrincipal] = useState<SelfServiceTeamsPrincipal | undefined>();
    const { t, i18n } = useTranslation();

    const {loading, theme, themeString, inTeams} = useTeamsHost();
    const credential = useMemo(
        () => new TeamsAdminCredential({clientId, initiateLoginEndpoint: "/auth-start.html"}),
        [clientId],
    );

    const fluentTheme = resolveFluentTheme(theme, themeString);
    const selfServiceSignupUrl = buildSelfServiceSignupUrl();

    useEffect(() => {
        persistTeamsThemeString(themeString);
        applyShellBackground(fluentTheme);
        document.documentElement.dataset.shellTheme =
            themeString === "dark" || themeString === "contrast" ? "dark" : "";
    }, [fluentTheme, themeString]);

    useEffect(() => {
        if (!loading && inTeams !== undefined) {
            removeInitialLoader();
        }
    }, [loading, inTeams]);

    const refreshApp = () => {
        window.location.replace(buildReloadUrl(window.location.origin, newVersionAvailable));
    };

    const dismissVersionBanner = () => {
        if (newVersionAvailable) {
            sessionStorage.setItem("admin-tab-dismissed-version", newVersionAvailable);
        }
        setNewVersionAvailable(null);
    };

    const checkAppVersion = async () => {
        try {
            const response = await fetch(`/version.json?_=${Date.now()}`, { cache: "no-store" });
            if (!response.ok) {
                return;
            }

            const { version: latestVersion } = await response.json() as { version?: string };
            if (typeof latestVersion !== "string" || !latestVersion) {
                return;
            }

            if (isNewerVersion(latestVersion, CURRENT_VERSION)) {
                const dismissed = sessionStorage.getItem("admin-tab-dismissed-version");
                if (dismissed !== latestVersion) {
                    setNewVersionAvailable(latestVersion);
                }
            } else {
                sessionStorage.removeItem("admin-tab-dismissed-version");
                setNewVersionAvailable(null);
            }
        } catch {
            // Version check is best-effort; keep running the current build.
        }
    };

    useEffect(() => {
        void checkAppVersion();
        const onVisible = () => {
            if (document.visibilityState === "visible") {
                void checkAppVersion();
            }
        };
        document.addEventListener("visibilitychange", onVisible);
        const interval = window.setInterval(() => void checkAppVersion(), VERSION_CHECK_INTERVAL_MS);
        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.clearInterval(interval);
        };
    }, []);


    useEffect(() => {
        if (inTeams !== undefined && !inTeams) {
            setErrorMessage("This app can only be used inside Teams")
        }
    }, [inTeams, setErrorMessage]);


    useEffect(() => {
        if (inTeams) {
            app.initialize()
                .then(() => app.getContext())
                .then(async context => {
                    setAppId(context?.app.appId?.toString());
                    setSelfServicePrincipal({
                        tenantId: context.user?.tenant?.id,
                        userId: context.user?.id,
                    });
                    await i18n.changeLanguage(context.app.locale?.split('-')[0] || 'en')
                })
                .then(() => setTeamsAdminCredential(credential))
                .then(() => app.notifySuccess())
                .then(() => setInitialized(true))
                .catch(() => setErrorMessage("Failed to initialize Teams app. Please try again later."))
        }
    }, [credential, inTeams, i18n, setAppId, setErrorMessage, setTeamsAdminCredential]);

    const onTabSelect = (_: SelectTabEvent, data: SelectTabData) => {
        setSelectedTab(data.value as typeof selectedTab);
    };

    const renderTabContent = () => {
        switch (selectedTab) {
            case "home":
                return <HomeTab selfServiceSignupUrl={selfServiceSignupUrl}/>;
            case "groups":
                return <AudiencesTab/>;
            case "messageSeries":
                return <SeriesTab selfServicePrincipal={selfServicePrincipal}/>;
            case "activeModules":
                return <EnrollmentsTab/>;
            default:
                return <HomeTab selfServiceSignupUrl={selfServiceSignupUrl}/>;
        }
    };

    const showOutsideTeamsError = !loading && inTeams === false && !!errorMessage;
    const showTeamsShell = !loading && inTeams && initialized;

    const selfServiceButton = (
        <Button
            appearance="subtle"
            as="a"
            href={selfServicePrincipal ? buildSelfServiceLoginUrl(selfServicePrincipal) : undefined}
            disabled={!selfServicePrincipal}
            target="_blank"
            rel="noreferrer"
        >
            {t("button.go-to-self-service")}
        </Button>
    );


    if (loading || inTeams === undefined) {
        return null;
    }

    return (
        <FluentProvider theme={fluentTheme} style={{background: tokens.colorNeutralBackground3, minHeight: '100vh'}}>
            {newVersionAvailable && (
                <MessageBar intent="info" style={{width: '100%', padding: '10px', position: 'sticky', top: 0, zIndex: 1000}}>
                    <MessageBarBody>
                        <MessageBarTitle>{t("New version available")}</MessageBarTitle>
                        {t("A new version of this application is available.")}
                    </MessageBarBody>
                    <MessageBarActions>
                        <Button appearance="primary" onClick={refreshApp}>
                            {t("Refresh")}
                        </Button>
                        <Button
                            aria-label="dismiss"
                            onClick={dismissVersionBanner}
                            appearance="transparent"
                            icon={<Dismiss16Regular />}
                        />
                    </MessageBarActions>
                </MessageBar>
            )}
            <div style={{padding: '20px'}}>
                {showOutsideTeamsError && (
                    <MessageBar intent="error" style={{marginBottom: '20px', width: '100%', padding: '10px'}}>
                        <MessageBarBody>
                            <MessageBarTitle>Error</MessageBarTitle>
                            {errorMessage}
                        </MessageBarBody>
                    </MessageBar>
                )}
                {showTeamsShell && errorMessage && (
                    <MessageBar  intent="error" style={{marginBottom: '20px', width: '100%', padding: '10px'}}>
                        <MessageBarBody>
                            <MessageBarTitle>Error</MessageBarTitle>
                            {errorMessage}
                        </MessageBarBody>
                        <MessageBarActions
                            containerAction={
                                <Button
                                    aria-label="dismiss"
                                    onClick={() => setErrorMessage(null)}
                                    appearance="transparent"
                                    icon={<Dismiss16Regular />}
                                />
                            }>
                        </MessageBarActions>
                    </MessageBar>
                )}
                {!loading && inTeams && !initialized && <Card><Spinner label={t("text.initializing")}></Spinner></Card>}
                {showTeamsShell && (
                    <SecuredWrapper
                        selfServiceButton={selfServiceButton}
                        selfServiceSignupUrl={selfServiceSignupUrl}
                    >
                        <>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px'}}>
                                <TabNavigation selectedValue={selectedTab} onTabSelect={onTabSelect}/>
                                {selfServiceButton}
                            </div>

                            <div style={{marginTop: '20px'}}>
                                <Suspense fallback={<Card><Spinner label={t("text.initializing")}/></Card>}>
                                    {renderTabContent()}
                                </Suspense>
                            </div>
                        </>
                    </SecuredWrapper>
                )}
            </div>
        </FluentProvider>
    );
}
